// src/common/utils/nameKanaMap.ts

/**
 * フルネーム（姓 + 名）とその読みを 1 箇所で管理するための定義。
 *
 * 「この苗字とこの名前がペアで 1 人」というのが一覧で分かるようにする。
 */
export type NameKanaDefinition = {
  family: string;
  familyKana: string;
  first: string;
  firstKana: string;
};

/**
 * 「姓・名」とその読みの一覧。
 *
 * ここを見れば「誰がどの姓名なのか」が一目で分かるようにする。
 * 追加・修正もこの配列だけを触ればよい。
 */
export const NAME_KANA_DEFINITIONS: NameKanaDefinition[] = [
  // ===== ここからデータ（姓・名） =====
  { family: '大池', familyKana: 'おおいけ', first: '敦夫', firstKana: 'あつお' },
  { family: '樋口', familyKana: 'ひぐち', first: '篤史', firstKana: 'あつし' },
  { family: '平池', familyKana: 'ひらいけ', first: '歩', firstKana: 'あゆむ' },
  { family: '磯村', familyKana: 'いそむら', first: '明那', firstKana: 'あんな' },
  { family: '神山', familyKana: 'かみやま', first: '敦嗣', firstKana: 'あつつぐ' },
  { family: '木下', familyKana: 'きのした', first: '輝', firstKana: 'あきら' },
  { family: '河野', familyKana: 'こうの', first: '綾子', firstKana: 'あやこ' },
  { family: '牧山', familyKana: 'まきやま', first: '明裕', firstKana: 'あきひろ' },
  { family: '森次', familyKana: 'もりつぐ', first: '晃弘', firstKana: 'あきひろ' },
  { family: '嶋田', familyKana: 'しまだ', first: '陽', firstKana: 'よう' },
  { family: '田岡', familyKana: 'たおか', first: '綾子', firstKana: 'あやこ' },
  { family: '豊島', familyKana: 'とよしま', first: '温', firstKana: 'はる' },
  { family: '米澤', familyKana: 'よねざわ', first: '耀', firstKana: 'よう' },
  {
    family: 'ベリエザ',
    familyKana: 'べりえざ',
    first: 'エイマヌエル ジョッシュ ゴンザレス',
    firstKana: 'えいまぬえる じょっしゅ ごんざれす',
  },
  { family: '石川', familyKana: 'いしかわ', first: '昭彦', firstKana: 'あきひこ' },
  { family: '久保田', familyKana: 'くぼた', first: '晃', firstKana: 'あきら' },
  { family: '小川', familyKana: 'おがわ', first: '明久', firstKana: 'あきひさ' },
  { family: '小島', familyKana: 'こじま', first: '章宏', firstKana: 'あきひろ' },
  { family: '松本', familyKana: 'まつもと', first: '昂大', firstKana: 'こうだい' },
  { family: '里', familyKana: 'さと', first: '明起照', firstKana: 'あきてる' },
  { family: '平野', familyKana: 'ひらの', first: '亮典', firstKana: 'あきのり' },
  { family: '稲葉', familyKana: 'いなば', first: '陽', firstKana: 'よう' },
  { family: '石川', familyKana: 'いしかわ', first: '晃', firstKana: 'あきら' },
  { family: '我妻', familyKana: 'あづま', first: '蒼生', firstKana: 'あおい' },
  { family: '有岡', familyKana: 'ありおか', first: '直', firstKana: 'なお' },
  { family: '和田', familyKana: 'わだ', first: '敦裕', firstKana: 'あつひろ' },
  { family: '永山', familyKana: 'ながやま', first: '敦規', firstKana: 'あつのり' },
  { family: '古宮', familyKana: 'ふるみや', first: '亜友美', firstKana: 'あゆみ' },
  { family: '別府', familyKana: 'べっぷ', first: '忍', firstKana: 'しのぶ' },
  { family: '藤田', familyKana: 'ふじた', first: '力', firstKana: 'ちから' },
  { family: '千葉', familyKana: 'ちば', first: '一史', firstKana: 'かずし' },
  { family: '知久', familyKana: 'ちく', first: '健二', firstKana: 'けんじ' },
  { family: '佐藤', familyKana: 'さとう', first: '直行', firstKana: 'なおゆき' },
  { family: '堀', familyKana: 'ほり', first: '太樹', firstKana: 'たいき' },
  { family: '四方', familyKana: 'しかた', first: '大暉', firstKana: 'だいき' },
  { family: '土屋', familyKana: 'つちや', first: '大介', firstKana: 'だいすけ' },
  { family: '伊藤', familyKana: 'いとう', first: '大輝', firstKana: 'だいき' },
  { family: '北林', familyKana: 'きたばやし', first: '大', firstKana: 'だい' },
  { family: '中山', familyKana: 'なかやま', first: '大', firstKana: 'だい' },
  { family: '葛西', familyKana: 'かさい', first: '大浄', firstKana: 'だいじょう' },
  { family: '村木', familyKana: 'むらき', first: '大輔', firstKana: 'だいすけ' },
  { family: '中島', familyKana: 'なかじま', first: '大介', firstKana: 'だいすけ' },
  { family: '内田', familyKana: 'うちだ', first: '大裕', firstKana: 'だいゆう' },
  { family: '出口', familyKana: 'でぐち', first: '博', firstKana: 'ひろし' },
  { family: '赤松', familyKana: 'あかまつ', first: '隆志', firstKana: 'たかし' },
  { family: '伊東', familyKana: 'いとう', first: '遼祐', firstKana: 'りょうすけ' },
  { family: 'ディオ', familyKana: 'でぃお', first: '林', firstKana: 'りん' },
  { family: '髙橋', familyKana: 'たかはし', first: '英仁', firstKana: 'ひでひと' },
  { family: '江口', familyKana: 'えぐち', first: '達也', firstKana: 'たつや' },
  { family: '外塚', familyKana: 'とづか', first: '英治', firstKana: 'えいじ' },
  { family: '榎本', familyKana: 'えのもと', first: '祐太', firstKana: 'ゆうた' },
  { family: '佐藤', familyKana: 'さとう', first: '絵莉', firstKana: 'えり' },
  { family: '上野', familyKana: 'うえの', first: '英里奈', firstKana: 'えりな' },
  { family: '上田', familyKana: 'うえだ', first: '悦広', firstKana: 'えつひろ' },
  { family: '藤田', familyKana: 'ふじた', first: '裕行', firstKana: 'ひろゆき' },
  { family: '福田', familyKana: 'ふくだ', first: '晟也', firstKana: 'せいや' },
  { family: '福田', familyKana: 'ふくだ', first: '俊一', firstKana: 'しゅんいち' },
  { family: '福島', familyKana: 'ふくしま', first: '千宝', firstKana: 'ちほ' },
  { family: '市川', familyKana: 'いちかわ', first: '文彦', firstKana: 'ふみひこ' },
  { family: '中尾', familyKana: 'なかお', first: '文武', firstKana: 'ふみたけ' },
  { family: '舟本', familyKana: 'ふなもと', first: '達雄', firstKana: 'たつお' },
  { family: '山田', familyKana: 'やまだ', first: '次郎', firstKana: 'じろう' },
  { family: '品川', familyKana: 'しながわ', first: '源太郎', firstKana: 'げんたろう' },
  { family: '吉澤', familyKana: 'よしざわ', first: '岳人', firstKana: 'がくと' },
  { family: '鈴木', familyKana: 'すずき', first: '吾郎', firstKana: 'ごろう' },
  { family: '後藤', familyKana: 'ごとう', first: '昌也', firstKana: 'まさや' },
  { family: '小松', familyKana: 'こまつ', first: '隼人', firstKana: 'はやと' },
  { family: '西島', familyKana: 'にしじま', first: '一志', firstKana: 'かずし' },
  { family: '鬼本', familyKana: 'おにもと', first: '大輝', firstKana: 'だいき' },
  { family: '青柳', familyKana: 'あおやぎ', first: '仁', firstKana: 'じん' },
  { family: '長谷部', familyKana: 'はせべ', first: '浩', firstKana: 'ひろし' },
  { family: '早坂', familyKana: 'はやさか', first: '宏樹', firstKana: 'ひろき' },
  { family: '川俣', familyKana: 'かわまた', first: '英之', firstKana: 'ひでゆき' },
  { family: '湊', familyKana: 'みなと', first: '裕行', firstKana: 'ひろゆき' },
  { family: '西田', familyKana: 'にしだ', first: '裕紀', firstKana: 'ひろき' },
  { family: '野村', familyKana: 'のむら', first: '英樹', firstKana: 'ひでき' },
  { family: '佐伯', familyKana: 'さえき', first: '秀司', firstKana: 'しゅうじ' },
  { family: '住吉', familyKana: 'すみよし', first: '寛', firstKana: 'ひろし' },
  { family: '高橋', familyKana: 'たかはし', first: '弘和', firstKana: 'ひろかず' },
  { family: '戸村', familyKana: 'とむら', first: '均', firstKana: 'ひとし' },
  { family: '脇坂', familyKana: 'わきさか', first: '広明', firstKana: 'ひろあき' },
  { family: '山口', familyKana: 'やまぐち', first: '博', firstKana: 'ひろし' },
  { family: '花井', familyKana: 'はない', first: '洋志', firstKana: 'ようじ' },
  { family: '安井', familyKana: 'やすい', first: '晴彦', firstKana: 'はるひこ' },
  { family: '岡田', familyKana: 'おかだ', first: '春輝', firstKana: 'はるき' },
  { family: '長谷川', familyKana: 'はせがわ', first: '卓也', firstKana: 'たくや' },
  { family: '泉', familyKana: 'いずみ', first: '隼人', firstKana: 'はやと' },
  { family: '加藤', familyKana: 'かとう', first: '久貴', firstKana: 'ひさたか' },
  { family: '川本', familyKana: 'かわもと', first: '洋', firstKana: 'よう' },
  { family: '塚本', familyKana: 'つかもと', first: '寿人', firstKana: 'としひと' },
  { family: '堀江', familyKana: 'ほりえ', first: '秀嗣', firstKana: 'ひでつぐ' },
  { family: '塩野', familyKana: 'しおの', first: '英彦', firstKana: 'ひでひこ' },
  { family: '工藤', familyKana: 'くどう', first: '秀樹', firstKana: 'ひでき' },
  { family: '山崎', familyKana: 'やまざき', first: '秀了', firstKana: 'ひでのり' },
  { family: '上平', familyKana: 'うえひら', first: '秀之', firstKana: 'ひでゆき' },
  { family: '安藤', familyKana: 'あんどう', first: '光', firstKana: 'ひかる' },
  { family: '藤田', familyKana: 'ふじた', first: '光', firstKana: 'ひかる' },
  { family: '三好', familyKana: 'みよし', first: '輝', firstKana: 'あきら' },
  { family: '久米', familyKana: 'くめ', first: '宏昌', firstKana: 'ひろまさ' },
  { family: '宿谷', familyKana: 'しゅくや', first: '弘光', firstKana: 'ひろみつ' },
  { family: '佐藤', familyKana: 'さとう', first: '広彬', firstKana: 'ひろあき' },
  { family: '田仲', familyKana: 'たなか', first: '浩明', firstKana: 'ひろあき' },
  { family: '中村', familyKana: 'なかむら', first: '大樹', firstKana: 'だいき' },
  { family: '伊澤', familyKana: 'いざわ', first: '広海', firstKana: 'ひろみ' },
  { family: '大中', familyKana: 'おおなか', first: '皓允', firstKana: 'あきまさ' },
  { family: '冨田', familyKana: 'とみた', first: '寛剛', firstKana: 'ひろたけ' },
  { family: '山中', familyKana: 'やまなか', first: '宏祥', firstKana: 'ひろよし' },
  { family: '岡本', familyKana: 'おかもと', first: '浩幸', firstKana: 'ひろゆき' },
  { family: '比留川', familyKana: 'ひるかわ', first: '政喜', firstKana: 'まさき' },
  { family: '青島', familyKana: 'あおしま', first: '久', firstKana: 'ひさし' },
  { family: '下田', familyKana: 'しもだ', first: '久嗣', firstKana: 'ひさつぐ' },
  { family: '堀', familyKana: 'ほり', first: '祐一郎', firstKana: 'ゆういちろう' },
  { family: '長谷川', familyKana: 'はせがわ', first: '功', firstKana: 'いさお' },
  { family: '石塚', familyKana: 'いしづか', first: '唯純', firstKana: 'ゆいじゅん' },
  { family: '常泰', familyKana: 'つねやす', first: '伊豆男', firstKana: 'いずお' },
  { family: '伊林', familyKana: 'いばやし', first: '恒太朗', firstKana: 'こうたろう' },
  { family: '池上', familyKana: 'いけがみ', first: '天', firstKana: 'てん' },
  { family: '井上', familyKana: 'いのうえ', first: '裕介', firstKana: 'ゆうすけ' },
  { family: '井上', familyKana: 'いのうえ', first: '勝', firstKana: 'まさる' },
  { family: '福島', familyKana: 'ふくしま', first: '一誠', firstKana: 'いっせい' },
  { family: '石渡', familyKana: 'いしわた', first: '幸治', firstKana: 'こうじ' },
  { family: '伊藤', familyKana: 'いとう', first: '涼', firstKana: 'りょう' },
  { family: '冨田', familyKana: 'とみた', first: '樹', firstKana: 'いつき' },
  { family: '赤坂', familyKana: 'あかさか', first: '順', firstKana: 'じゅん' },
  { family: '若松', familyKana: 'わかまつ', first: '純一', firstKana: 'じゅんいち' },
  { family: '吉住', familyKana: 'よしずみ', first: '純一', firstKana: 'じゅんいち' },
  { family: '林', familyKana: 'はやし', first: '家華', firstKana: 'いえか' },
  { family: '野崎', familyKana: 'のざき', first: '光司', firstKana: 'こうじ' },
  { family: '舟坂', familyKana: 'ふなさか', first: '和也', firstKana: 'かずや' },
  { family: '平谷', familyKana: 'ひらたに', first: '康治', firstKana: 'こうじ' },
  { family: '神野', familyKana: 'じんの', first: '圭造', firstKana: 'けいぞう' },
  { family: '鎌塚', familyKana: 'かまつか', first: '均', firstKana: 'ひとし' },
  { family: '小森', familyKana: 'こもり', first: '一宗', firstKana: 'かずむね' },
  { family: '松田', familyKana: 'まつだ', first: '一輝', firstKana: 'かずき' },
  { family: '松野', familyKana: 'まつの', first: '一也', firstKana: 'かずや' },
  { family: '諸', familyKana: 'もろ', first: '光司', firstKana: 'こうじ' },
  { family: '長浜', familyKana: 'ながはま', first: '広平', firstKana: 'こうへい' },
  { family: '中辻', familyKana: 'なかつじ', first: '幸太', firstKana: 'こうた' },
  { family: '尾﨑', familyKana: 'おざき', first: '幸子', firstKana: 'さちこ' },
  { family: '塩田', familyKana: 'しおだ', first: '圭祐', firstKana: 'けいすけ' },
  { family: '高橋', familyKana: 'たかはし', first: '健太郎', firstKana: 'けんたろう' },
  { family: '嶽澤', familyKana: 'たけざわ', first: '一彰', firstKana: 'かずあき' },
  { family: '前島', familyKana: 'まえじま', first: '和彰', firstKana: 'かずあき' },
  { family: '門野', familyKana: 'かどの', first: '武士', firstKana: 'たけし' },
  { family: '池田', familyKana: 'いけだ', first: '開', firstKana: 'かい' },
  { family: '海﨑', familyKana: 'かいざき', first: '泰世', firstKana: 'たいせい' },
  { family: '郭', familyKana: 'かく', first: '玲', firstKana: 'れい' },
  { family: '亀井', familyKana: 'かめい', first: '健太', firstKana: 'けんた' },
  { family: '神長', familyKana: 'かみなが', first: '俊基', firstKana: 'としき' },
  { family: '吉川', familyKana: 'よしかわ', first: '香菜', firstKana: 'かな' },
  { family: '白水', familyKana: 'しろうず', first: '哉大', firstKana: 'かいと' },
  { family: '神田', familyKana: 'かんだ', first: '友也', firstKana: 'ともや' },
  { family: '菅田', familyKana: 'すげた', first: '安洋', firstKana: 'やすひろ' },
  { family: '池田', familyKana: 'いけだ', first: '勘太郎', firstKana: 'かんたろう' },
  { family: '樫本', familyKana: 'かしもと', first: '直記', firstKana: 'なおき' },
  { family: '勝島', familyKana: 'かつしま', first: '悠太', firstKana: 'ゆうた' },
  { family: '川口', familyKana: 'かわぐち', first: '貴之', firstKana: 'たかゆき' },
  { family: '川尻', familyKana: 'かわじり', first: '和宏', firstKana: 'かずひろ' },
  { family: '山岸', familyKana: 'やまぎし', first: '一成', firstKana: 'かずなり' },
  { family: '小倉', familyKana: 'おぐら', first: '風樹', firstKana: 'ふうき' },
  { family: '井上', familyKana: 'いのうえ', first: '和司', firstKana: 'かずし' },
  { family: '松本', familyKana: 'まつもと', first: '和博', firstKana: 'かずひろ' },
  { family: '村上', familyKana: 'むらかみ', first: '和紀', firstKana: 'かずのり' },
  { family: '中山', familyKana: 'なかやま', first: '和貴', firstKana: 'かずたか' },
  { family: '南部', familyKana: 'なんぶ', first: '和士', firstKana: 'かずし' },
  { family: '小林', familyKana: 'こばやし', first: '一晴', firstKana: 'かずはる' },
  { family: '藤本', familyKana: 'ふじもと', first: '和希', firstKana: 'かずき' },
  { family: '上田', familyKana: 'うえだ', first: '和貴', firstKana: 'かずたか' },
  { family: '鈴木', familyKana: 'すずき', first: '和成', firstKana: 'かずなり' },
  { family: '橋本', familyKana: 'はしもと', first: '和弥', firstKana: 'かずや' },
  { family: '国本', familyKana: 'くにもと', first: '健汰', firstKana: 'けんた' },
  { family: '鳥居', familyKana: 'とりい', first: '健太郎', firstKana: 'けんたろう' },
  { family: '西嶋', familyKana: 'にしじま', first: '慶', firstKana: 'けい' },
  { family: '大原', familyKana: 'おおはら', first: '渓介', firstKana: 'けいすけ' },
  { family: '西澤', familyKana: 'にしざわ', first: '圭悟', firstKana: 'けいご' },
  { family: '酒井', familyKana: 'さかい', first: '圭一', firstKana: 'けいいち' },
  { family: '小島', familyKana: 'こじま', first: '啓介', firstKana: 'けいすけ' },
  { family: '森山', familyKana: 'もりやま', first: '恵介', firstKana: 'けいすけ' },
  { family: '森', familyKana: 'もり', first: '啓太', firstKana: 'けいた' },
  { family: '樋口', familyKana: 'ひぐち', first: '恵也', firstKana: 'けいや' },
  { family: '伊藤', familyKana: 'いとう', first: '研', firstKana: 'けん' },
  { family: '櫻井', familyKana: 'さくらい', first: '健治', firstKana: 'けんじ' },
  { family: '佐々木', familyKana: 'ささき', first: '賢二', firstKana: 'けんじ' },
  { family: '深渡', familyKana: 'ふかど', first: '健悟', firstKana: 'けんご' },
  { family: '寺田', familyKana: 'てらだ', first: '健一郎', firstKana: 'けんいちろう' },
  { family: '前田', familyKana: 'まえだ', first: '健司', firstKana: 'けんじ' },
  { family: '徳永', familyKana: 'とくなが', first: '紀京', firstKana: 'のりきょう' },
  { family: '木村', familyKana: 'きむら', first: '碩志', firstKana: 'ひろし' },
  { family: '川渕', familyKana: 'かわぶち', first: '希星', firstKana: 'きらら' },
  { family: '福田', familyKana: 'ふくだ', first: '清道', firstKana: 'きよみち' },
  { family: '中村', familyKana: 'なかむら', first: '剛士', firstKana: 'つよし' },
  { family: '村上', familyKana: 'むらかみ', first: '健太', firstKana: 'けんた' },
  { family: '河村', familyKana: 'かわむら', first: '康佑', firstKana: 'こうすけ' },
  { family: '岡部', familyKana: 'おかべ', first: '耕司', firstKana: 'こうじ' },
  { family: '小野寺', familyKana: 'おのでら', first: '恒', firstKana: 'つね' },
  { family: '小林', familyKana: 'こばやし', first: '克博', firstKana: 'かつひろ' },
  { family: '小林', familyKana: 'こばやし', first: '祐紀', firstKana: 'ゆうき' },
  { family: '田代', familyKana: 'たしろ', first: '航大', firstKana: 'こうだい' },

  // ===== ここから今回追加分 =====
  { family: '川上', familyKana: 'かわかみ', first: '晃平', firstKana: 'こうへい' },
  { family: '高橋', familyKana: 'たかはし', first: '頌平', firstKana: 'しょうへい' },
  { family: '田中', familyKana: 'たなか', first: '耕一郎', firstKana: 'こういちろう' },
  { family: '河田', familyKana: 'かわだ', first: '浩次', firstKana: 'こうじ' },
  { family: '小島', familyKana: 'こじま', first: '敦', firstKana: 'あつし' },
  { family: '纐纈', familyKana: 'こうけつ', first: '晋也', firstKana: 'しんや' },
  { family: '中村', familyKana: 'なかむら', first: '皓聖', firstKana: 'こうせい' },
  { family: '越石', familyKana: 'こしいし', first: '絢太郎', firstKana: 'あやたろう' },
  { family: '佐藤', familyKana: 'さとう', first: '広介', firstKana: 'こうすけ' },
  { family: '眞壁', familyKana: 'まかべ', first: '邦嘉', firstKana: 'くによし' },
  { family: '倉見', familyKana: 'くらみ', first: '洋平', firstKana: 'ようへい' },
  { family: '草部', familyKana: 'くさべ', first: '裕幸', firstKana: 'ひろゆき' },
  { family: '臼井', familyKana: 'うすい', first: '亨平', firstKana: 'こうへい' },
  { family: '李', familyKana: 'り', first: '文斌', firstKana: 'ぶんひん' },
  { family: '大滝', familyKana: 'おおたき', first: '琉久', firstKana: 'りく' },
  { family: '長谷川', familyKana: 'はせがわ', first: '将也', firstKana: 'まさや' },
  { family: '秋山', familyKana: 'あきやま', first: '宗之', firstKana: 'むねゆき' },
  { family: '浅水', familyKana: 'あさみず', first: '勝', firstKana: 'まさる' },
  { family: '萬', familyKana: 'よろず', first: '志揚', firstKana: 'しよう' },
  { family: '遠藤', familyKana: 'えんどう', first: '満之', firstKana: 'みつゆき' },
  { family: '福垣', familyKana: 'ふくがき', first: '麻里', firstKana: 'まり' },
  { family: '古屋', familyKana: 'ふるや', first: '雅則', firstKana: 'まさのり' },
  { family: '服部', familyKana: 'はっとり', first: '正志', firstKana: 'まさし' },
  { family: '岩塚', familyKana: 'いわつか', first: '美智子', firstKana: 'みちこ' },
  { family: '金山', familyKana: 'かなやま', first: '雅洋', firstKana: 'まさひろ' },
  { family: '西端', familyKana: 'にしばた', first: '政宏', firstKana: 'まさひろ' },
  { family: '尾幡', familyKana: 'おばた', first: '昌芳', firstKana: 'まさよし' },
  { family: '小見', familyKana: 'おみ', first: '昌義', firstKana: 'まさよし' },
  { family: '谷本', familyKana: 'たにもと', first: '守', firstKana: 'まもる' },
  { family: '宇津木', familyKana: 'うつぎ', first: '正之', firstKana: 'まさゆき' },
  { family: '馬場', familyKana: 'ばば', first: '真理子', firstKana: 'まりこ' },
  { family: '小向', familyKana: 'こむかい', first: '学', firstKana: 'まなぶ' },
  { family: '森田', familyKana: 'もりた', first: '正幸', firstKana: 'まさゆき' },
  { family: '北本', familyKana: 'きたもと', first: '眞大', firstKana: 'まさひろ' },
  { family: '石原', familyKana: 'いしはら', first: '真希', firstKana: 'まき' },
  { family: '渡邉', familyKana: 'わたなべ', first: '真季', firstKana: 'まき' },
  { family: '小柏', familyKana: 'こがしわ', first: '真', firstKana: 'まこと' },
  { family: '佐藤', familyKana: 'さとう', first: '誠', firstKana: 'まこと' },
  { family: '谷村', familyKana: 'たにむら', first: '真', firstKana: 'まこと' },
  { family: '櫻木', familyKana: 'さくらぎ', first: '麻美子', firstKana: 'まみこ' },
  { family: '山下', familyKana: 'やました', first: '真央', firstKana: 'まお' },
  { family: '丸勢', familyKana: 'まるせ', first: '修', firstKana: 'おさむ' },
  { family: '丸山', familyKana: 'まるやま', first: '勝司', firstKana: 'まさし' },
  { family: '入江', familyKana: 'いりえ', first: '雅之', firstKana: 'まさゆき' },
  { family: '松川', familyKana: 'まつかわ', first: '政之', firstKana: 'まさゆき' },
  { family: '立花', familyKana: 'たちばな', first: '正統', firstKana: 'まさのり' },
  { family: '金子', familyKana: 'かねこ', first: '真大', firstKana: 'まさひろ' },
  { family: '岡本', familyKana: 'おかもと', first: '正宏', firstKana: 'まさひろ' },
  { family: '福田', familyKana: 'ふくだ', first: '将生', firstKana: 'まさき' },
  { family: '川橋', familyKana: 'かわはし', first: '正樹', firstKana: 'まさき' },
  { family: '北野', familyKana: 'きたの', first: 'まさみ', firstKana: 'まさみ' },
  { family: '須藤', familyKana: 'すどう', first: '勝', firstKana: 'まさる' },
  { family: '清水', familyKana: 'しみず', first: '雅人', firstKana: 'まさと' },
  { family: '加藤', familyKana: 'かとう', first: '将俊', firstKana: 'まさとし' },
  { family: '松原', familyKana: 'まつばら', first: '鉄也', firstKana: 'てつや' },
  { family: '松本', familyKana: 'まつもと', first: '卓也', firstKana: 'たくや' },
  { family: '松永', familyKana: 'まつなが', first: '亮', firstKana: 'りょう' },
  { family: '松下', familyKana: 'まつした', first: '晃洋', firstKana: 'あきひろ' },
  { family: '仲', familyKana: 'なか', first: '真弓', firstKana: 'まゆみ' },
  { family: '大島', familyKana: 'おおしま', first: '実穂', firstKana: 'みほ' },
  { family: '芳賀', familyKana: 'はが', first: '南美', firstKana: 'なみ' },
  { family: '小倉', familyKana: 'おぐら', first: '実', firstKana: 'みのる' },
  { family: '太田', familyKana: 'おおた', first: '美良乃', firstKana: 'みらの' },
  { family: '後藤', familyKana: 'ごとう', first: '光治', firstKana: 'みつはる' },
  { family: '宮川', familyKana: 'みやがわ', first: '拓巳', firstKana: 'たくみ' },
  { family: '宮島', familyKana: 'みやじま', first: '暁大', firstKana: 'あきひろ' },
  { family: '大島', familyKana: 'おおしま', first: '正道', firstKana: 'まさみち' },
  { family: '柳澤', familyKana: 'やなぎさわ', first: '基浩', firstKana: 'もとひろ' },
  { family: '森田', familyKana: 'もりた', first: '哲男', firstKana: 'てつお' },
  { family: '佐藤', familyKana: 'さとう', first: '勝', firstKana: 'まさる' },
  { family: '向川', familyKana: 'むかいがわ', first: '大樹', firstKana: 'だいき' },
  { family: '鈴木', familyKana: 'すずき', first: '宗浩', firstKana: 'むねひろ' },
  { family: '武藤', familyKana: 'むとう', first: '太郎', firstKana: 'たろう' },
  { family: '永井', familyKana: 'ながい', first: '久博', firstKana: 'ひさひろ' },
  { family: '宮川', familyKana: 'みやがわ', first: '尚紀', firstKana: 'なおき' },
  { family: '齋藤', familyKana: 'さいとう', first: '尚子', firstKana: 'なおこ' },
  { family: '瀬尾', familyKana: 'せお', first: '直也', firstKana: 'なおや' },
  { family: '田中', familyKana: 'たなか', first: '直美', firstKana: 'なおみ' },
  { family: '山本', familyKana: 'やまもと', first: '直美', firstKana: 'なおみ' },
  { family: '中嶋', familyKana: 'なかじま', first: '健二', firstKana: 'けんじ' },
  { family: '彌永', familyKana: 'いやなが', first: '尚尭', firstKana: 'なおたか' },
  { family: '兼久', familyKana: 'かねひさ', first: '尚樹', firstKana: 'なおき' },
  { family: '西谷', familyKana: 'にしたに', first: '尚起', firstKana: 'なおき' },
  { family: '桑澤', familyKana: 'くわざわ', first: '直哉', firstKana: 'なおや' },
  { family: '前川', familyKana: 'まえかわ', first: '直之', firstKana: 'なおゆき' },
  { family: '小池', familyKana: 'こいけ', first: '成顕', firstKana: 'しげあき' },
  { family: '柴野', familyKana: 'しばの', first: '夏海', firstKana: 'なつみ' },
  { family: '加藤', familyKana: 'かとう', first: '新', firstKana: 'あらた' },
  { family: '西村', familyKana: 'にしむら', first: '宣哉', firstKana: 'のりや' },
  { family: '川端', familyKana: 'かわばた', first: '規之', firstKana: 'のりゆき' },
  { family: '近藤', familyKana: 'こんどう', first: '典行', firstKana: 'のりゆき' },
  { family: '宮川', familyKana: 'みやがわ', first: '紀子', firstKana: 'のりこ' },
  { family: '池戸', familyKana: 'いけど', first: '憲夫', firstKana: 'のりお' },
  { family: '小川', familyKana: 'おがわ', first: '夏実', firstKana: 'なつみ' },
  { family: '荻原', familyKana: 'おぎわら', first: '瑛彦', firstKana: 'あきひこ' },
  { family: '小倉', familyKana: 'おぐら', first: '怜', firstKana: 'れい' },
  { family: '小原', familyKana: 'こはら', first: '英之', firstKana: 'ひでゆき' },
  { family: '大石', familyKana: 'おおいし', first: '充希', firstKana: 'みつき' },
  { family: '井上', familyKana: 'いのうえ', first: '王貴', firstKana: 'おうき' },
  { family: '奥原', familyKana: 'おくはら', first: '裕幸', firstKana: 'ひろゆき' },
  { family: '小野', familyKana: 'おの', first: '修二郎', firstKana: 'しゅうじろう' },
  { family: '大武', familyKana: 'おおたけ', first: '祐貴', firstKana: 'ゆうき' },
  { family: '尾崎', familyKana: 'おざき', first: '輝', firstKana: 'あきら' },
  { family: '井野', familyKana: 'いの', first: '龍之介', firstKana: 'りゅうのすけ' },
  { family: '石原', familyKana: 'いしはら', first: '亮', firstKana: 'りょう' },
  { family: '香月', familyKana: 'かづき', first: '亮二', firstKana: 'りょうじ' },
  { family: '高橋', familyKana: 'たかはし', first: '了', firstKana: 'りょう' },
  { family: '玉木', familyKana: 'たまき', first: '良典', firstKana: 'よしのり' },
  { family: '渡部', familyKana: 'わたなべ', first: '理恵', firstKana: 'りえ' },
  { family: '力丸', familyKana: 'りきまる', first: '亮平', firstKana: 'りょうへい' },
  { family: '関根', familyKana: 'せきね', first: '立弓', firstKana: 'たつゆみ' },
  { family: '江口', familyKana: 'えぐち', first: '陸生', firstKana: 'りくお' },
  { family: '服巻', familyKana: 'はらまき', first: '利奈', firstKana: 'りな' },
  { family: '竹内', familyKana: 'たけうち', first: '亮郎', firstKana: 'りょうろう' },
  { family: '西野', familyKana: 'にしの', first: '領', firstKana: 'りょう' },
  { family: '渡邉', familyKana: 'わたなべ', first: '凌', firstKana: 'りょう' },
  { family: '酒井', familyKana: 'さかい', first: '遼馬', firstKana: 'りょうま' },
  { family: '高橋', familyKana: 'たかはし', first: '涼真', firstKana: 'りょうま' },
  { family: '六岡', familyKana: 'ろくおか', first: '諒介', firstKana: 'りょうすけ' },
  { family: '新納', familyKana: 'にいの', first: '僚太', firstKana: 'りょうた' },
  { family: '荒木', familyKana: 'あらき', first: '諒', firstKana: 'りょう' },
  { family: '中田', familyKana: 'なかた', first: '亮', firstKana: 'りょう' },
  { family: '鈴木', familyKana: 'すずき', first: '諒', firstKana: 'りょう' },
  { family: '井手', familyKana: 'いで', first: '良太郎', firstKana: 'りょうたろう' },
  { family: '酒井', familyKana: 'さかい', first: '琉之介', firstKana: 'りゅうのすけ' },
  { family: '山崎', familyKana: 'やまざき', first: '流斗', firstKana: 'りゅうと' },
  { family: '青栁', familyKana: 'あおやなぎ', first: '龍三', firstKana: 'りゅうぞう' },
  { family: '野口', familyKana: 'のぐち', first: '隆三', firstKana: 'りゅうぞう' },
  { family: '塚田', familyKana: 'つかだ', first: '祥大', firstKana: 'しょうた' },
  { family: '堂守', familyKana: 'どうもり', first: '将', firstKana: 'しょう' },
  { family: '古尾谷', familyKana: 'ふるおや', first: '信吾', firstKana: 'しんご' },
  { family: '郡司', familyKana: 'ぐんじ', first: '成則', firstKana: 'なりのり' },
  { family: '日比', familyKana: 'ひび', first: '慎也', firstKana: 'しんや' },
  { family: '日向寺', familyKana: 'ひなたじ', first: '俊輔', firstKana: 'しゅんすけ' },
  { family: '春日', familyKana: 'かすが', first: '慧士', firstKana: 'けいし' },
  { family: '川崎', familyKana: 'かわさき', first: '真二', firstKana: 'しんじ' },
  { family: '宗藤', familyKana: 'そうどう', first: '総司', firstKana: 'そうじ' },
  { family: '成田', familyKana: 'なりた', first: '真吾', firstKana: 'しんご' },
  { family: '仁平', familyKana: 'にへい', first: '智', firstKana: 'さとし' },
  { family: '佐久間', familyKana: 'さくま', first: '宇宙', firstKana: 'そら' },
  { family: '高辻', familyKana: 'たかつじ', first: '茂', firstKana: 'しげる' },
  { family: '手島', familyKana: 'てしま', first: '聡', firstKana: 'さとし' },
  { family: '山下', familyKana: 'やました', first: '彩', firstKana: 'あや' },
  { family: '安井', familyKana: 'やすい', first: '定', firstKana: 'さだむ' },
  { family: '淡路', familyKana: 'あわじ', first: 'さつき', firstKana: 'さつき' },
  { family: '齋藤', familyKana: 'さいとう', first: '雄太', firstKana: 'ゆうた' },
  { family: '坂', familyKana: 'さか', first: '直樹', firstKana: 'なおき' },
  { family: '坂元', familyKana: 'さかもと', first: '勝彦', firstKana: 'かつひこ' },
  { family: '坂本', familyKana: 'さかもと', first: '宇翔', firstKana: 'たかと' },
  { family: '金子', familyKana: 'かねこ', first: '敏志', firstKana: 'さとし' },
  { family: '伊神', familyKana: 'いがみ', first: '智美', firstKana: 'ともみ' },
  { family: '佐藤', familyKana: 'さとう', first: '智喜', firstKana: 'ともき' },
  { family: '杉山', familyKana: 'すぎやま', first: '精一', firstKana: 'せいいち' },
  { family: '瀬下', familyKana: 'せした', first: '武', firstKana: 'たけし' },
  { family: '成田', familyKana: 'なりた', first: '慎吾', firstKana: 'しんご' },
  { family: '中嶋', familyKana: 'なかじま', first: '真司', firstKana: 'しんじ' },
  { family: '清水', familyKana: 'しみず', first: '俊一', firstKana: 'しゅんいち' },
  { family: '森本', familyKana: 'もりもと', first: '真也', firstKana: 'しんや' },
  { family: '小野寺', familyKana: 'おのでら', first: '真一', firstKana: 'しんいち' },
  { family: '細部', familyKana: 'さいぶ', first: '真吾', firstKana: 'しんご' },
  { family: '田中', familyKana: 'たなか', first: '真悟', firstKana: 'しんご' },
  { family: '佐藤', familyKana: 'さとう', first: '忍', firstKana: 'しのぶ' },
  { family: '上村', familyKana: 'うえむら', first: '忍', firstKana: 'しのぶ' },
  { family: '山﨑', familyKana: 'やまざき', first: '信哉', firstKana: 'しんや' },
  { family: '塩崎', familyKana: 'しおざき', first: '敦史', firstKana: 'あつし' },
  { family: '中村', familyKana: 'なかむら', first: '静男', firstKana: 'しずお' },
  { family: '山本', familyKana: 'やまもと', first: '翔', firstKana: 'しょう' },
  { family: '中尾', familyKana: 'なかお', first: '修造', firstKana: 'しゅうぞう' },
  { family: '折茂', familyKana: 'おりも', first: '秀一', firstKana: 'しゅういち' },
  { family: '木村', familyKana: 'きむら', first: '修吾', firstKana: 'しゅうご' },
  { family: 'suzuki', familyKana: 'すずき', first: 'shuji', firstKana: 'しゅうじ' },
  { family: '藤田', familyKana: 'ふじた', first: '駿', firstKana: 'しゅん' },
  { family: '伊藤', familyKana: 'いとう', first: '隼', firstKana: 'しゅん' },
  { family: '江畑', familyKana: 'えばた', first: '俊介', firstKana: 'しゅんすけ' },
  { family: '井上', familyKana: 'いのうえ', first: '隼輔', firstKana: 'しゅんすけ' },
  { family: '中町', familyKana: 'なかまち', first: '俊介', firstKana: 'しゅんすけ' },
  { family: '山本', familyKana: 'やまもと', first: '駿介', firstKana: 'しゅんすけ' },
  { family: '有田', familyKana: 'ありた', first: '駿介', firstKana: 'しゅんすけ' },
  { family: '程', familyKana: 'てい', first: '思達', firstKana: 'したつ' },
  { family: '中村', familyKana: 'なかむら', first: '新二', firstKana: 'しんじ' },
  { family: '長江', familyKana: 'ながえ', first: '新平', firstKana: 'しんぺい' },
  { family: '梨本', familyKana: 'なしもと', first: '伸樹', firstKana: 'のぶき' },
  { family: '岩瀬', familyKana: 'いわせ', first: '蒼太', firstKana: 'そうた' },
  { family: '川久保', familyKana: 'かわくぼ', first: '颯太', firstKana: 'そうた' },
  { family: '飯塚', familyKana: 'いいづか', first: '哲史', firstKana: 'てつし' },
  { family: '杉村', familyKana: 'すぎむら', first: '僚大', firstKana: 'りょうた' },
  { family: '岡田', familyKana: 'おかだ', first: '直', firstKana: 'なお' },
  { family: '安', familyKana: 'あん', first: '蘇陽', firstKana: 'そよう' },
  { family: '鈴木', familyKana: 'すずき', first: '岳', firstKana: 'がく' },
  { family: '鈴木', familyKana: 'すずき', first: '寿治', firstKana: 'ひさはる' },
  { family: '髙橋', familyKana: 'たかはし', first: '修平', firstKana: 'しゅうへい' },
  { family: '沢田', familyKana: 'さわだ', first: '孝之', firstKana: 'たかゆき' },
  { family: '茶谷', familyKana: 'ちゃや', first: '恒明', firstKana: 'つねあき' },
  { family: '林原', familyKana: 'はやしばら', first: '富之', firstKana: 'とみゆき' },
  { family: '平塚', familyKana: 'ひらつか', first: '剛', firstKana: 'つよし' },
  { family: '神庭', familyKana: 'かんば', first: '拓斗', firstKana: 'たくと' },
  { family: '香山', familyKana: 'かやま', first: '敬行', firstKana: 'たかゆき' },
  { family: '小林', familyKana: 'こばやし', first: '珠惠', firstKana: 'たまえ' },
  { family: '田口', familyKana: 'たぐち', first: '雅章', firstKana: 'まさあき' },
  { family: '邨井', familyKana: 'むらい', first: '壮', firstKana: 'そう' },
  { family: '根岸', familyKana: 'ねぎし', first: '司', firstKana: 'つかさ' },
  { family: '大川', familyKana: 'おおかわ', first: '正', firstKana: 'ただし' },
  { family: '小座間', familyKana: 'こざま', first: '健', firstKana: 'たけし' },
  { family: '西條', familyKana: 'さいじょう', first: '貴穂', firstKana: 'たかほ' },
  { family: '菅野', familyKana: 'すがの', first: '哲夫', firstKana: 'てつお' },
  { family: '高橋', familyKana: 'たかはし', first: '瞳子', firstKana: 'とうこ' },
  { family: '玉造', familyKana: 'たまつくり', first: '隆徳', firstKana: 'たかのり' },
  { family: '谷本', familyKana: 'たにもと', first: '高雄', firstKana: 'たかお' },
  { family: '髙山', familyKana: 'たかやま', first: '辰徳', firstKana: 'たつのり' },
  { family: '薄井', familyKana: 'うすい', first: '智幸', firstKana: 'ともゆき' },
  { family: '堀', familyKana: 'ほり', first: '達郎', firstKana: 'たつろう' },
  { family: '川尻', familyKana: 'かわじり', first: '拓巳', firstKana: 'たくみ' },
  { family: '関谷', familyKana: 'せきや', first: '龍紀', firstKana: 'りゅうき' },
  { family: '田畑', familyKana: 'たばた', first: '仁', firstKana: 'じん' },
  { family: '多田', familyKana: 'ただ', first: '順一', firstKana: 'じゅんいち' },
  { family: 'キム', familyKana: 'きむ', first: 'テフン', firstKana: 'てふん' },
  { family: '平田', familyKana: 'ひらた', first: '昂大', firstKana: 'あきひろ' },
  { family: '内海', familyKana: 'うつみ', first: '崇行', firstKana: 'たかゆき' },
  { family: '高橋', familyKana: 'たかはし', first: '亨輔', firstKana: 'こうすけ' },
  { family: '高橋', familyKana: 'たかはし', first: '悠貴', firstKana: 'ゆうき' },
  { family: '岡﨑', familyKana: 'おかざき', first: '貴広', firstKana: 'たかひろ' },
  { family: '飯田', familyKana: 'いいだ', first: '隆寛', firstKana: 'たかひろ' },
  { family: '上脇', familyKana: 'うえわき', first: '崇弘', firstKana: 'たかひろ' },
  { family: '増田', familyKana: 'ますだ', first: '昂弘', firstKana: 'たかひろ' },
  { family: '橋本', familyKana: 'はしもと', first: '鷹則', firstKana: 'たかのり' },
  { family: '佐藤', familyKana: 'さとう', first: '孝諭', firstKana: 'たかゆ' },
  { family: '山崎', familyKana: 'やまざき', first: '貴義', firstKana: 'たかよし' },
  { family: '内藤', familyKana: 'ないとう', first: '丈裕', firstKana: 'たけひろ' },
  { family: '山中', familyKana: 'やまなか', first: '武', firstKana: 'たけし' },
  { family: '鈴木', familyKana: 'すずき', first: '健文', firstKana: 'たけふみ' },
  { family: '岡本', familyKana: 'おかもと', first: '壮司', firstKana: 'そうじ' },
  { family: '佐藤', familyKana: 'さとう', first: 'たけし', firstKana: 'たけし' },
  { family: '竹下', familyKana: 'たけした', first: '敏彦', firstKana: 'としひこ' },
  { family: '野村', familyKana: 'のむら', first: '拓史', firstKana: 'たくし' },
  { family: '吉岡', familyKana: 'よしおか', first: '卓哉', firstKana: 'たくや' },
  { family: '山下', familyKana: 'やました', first: '拓時', firstKana: 'たくじ' },
  { family: '内山', familyKana: 'うちやま', first: '琢也', firstKana: 'たくや' },
  { family: '田中', familyKana: 'たなか', first: '和衛', firstKana: 'かずえ' },
  { family: '田中', familyKana: 'たなか', first: '孝二', firstKana: 'こうじ' },
  { family: '橋本', familyKana: 'はしもと', first: '竜英', firstKana: 'たつひで' },
  { family: '三宅', familyKana: 'みやけ', first: '達也', firstKana: 'たつや' },
  { family: '下川', familyKana: 'しもかわ', first: '達也', firstKana: 'たつや' },
  { family: '南', familyKana: 'みなみ', first: '達矢', firstKana: 'たつや' },
  { family: '村松', familyKana: 'むらまつ', first: '哲史', firstKana: 'てつし' },
  { family: 'グエン', familyKana: 'ぐえん', first: 'テイジャン', firstKana: 'ていじゃん' },
  { family: '遠藤', familyKana: 'えんどう', first: '友久', firstKana: 'ともひさ' },
  { family: '新留', familyKana: 'にいどめ', first: '智明', firstKana: 'ともあき' },
  { family: '長田', familyKana: 'ながた', first: '俊明', firstKana: 'としあき' },
  { family: '寺田', familyKana: 'てらだ', first: '冬馬', firstKana: 'とうま' },
  { family: '富樫', familyKana: 'とがし', first: '佳世', firstKana: 'かよ' },
  { family: '内海', familyKana: 'うつみ', first: '智弘', firstKana: 'ともひろ' },
  { family: '溝口', familyKana: 'みぞぐち', first: '智久', firstKana: 'ともひさ' },
  { family: '横溝', familyKana: 'よこみぞ', first: '智紀', firstKana: 'ともき' },
  { family: '五十嵐', familyKana: 'いがらし', first: '智貴', firstKana: 'ともたか' },
  { family: '渡辺', familyKana: 'わたなべ', first: '亨', firstKana: 'とおる' },
  { family: '鳥川', familyKana: 'とりかわ', first: '伸之', firstKana: 'のぶゆき' },
  { family: '宇津野', familyKana: 'うつの', first: '透', firstKana: 'とおる' },
  { family: '玉井', familyKana: 'たまい', first: '稔久', firstKana: 'としく' },
  { family: '武居', familyKana: 'たけい', first: '敏生', firstKana: 'としお' },
  { family: '麻生', familyKana: 'あそう', first: '利哉', firstKana: 'としや' },
  { family: '石垣', familyKana: 'いしがき', first: '輝彦', firstKana: 'てるひこ' },
  { family: '大塚', familyKana: 'おおつか', first: '剛', firstKana: 'つよし' },
  { family: '田巻', familyKana: 'たまき', first: '翼', firstKana: 'つばさ' },
  { family: '座覇', familyKana: 'ざは', first: '翼', firstKana: 'つばさ' },
  { family: '塚原', familyKana: 'つかはら', first: '徹', firstKana: 'とおる' },
  { family: '諏訪', familyKana: 'すわ', first: '司', firstKana: 'つかさ' },
  { family: '村山', familyKana: 'むらやま', first: '司', firstKana: 'つかさ' },
  { family: '津村', familyKana: 'つむら', first: '駿介', firstKana: 'しゅんすけ' },
  { family: '田', familyKana: 'でん', first: '暾華', firstKana: 'とんか' },
  { family: '上田', familyKana: 'うえだ', first: '彬人', firstKana: 'あきと' },
  { family: '植木', familyKana: 'うえき', first: '由記子', firstKana: 'ゆきこ' },
  { family: '宇佐見', familyKana: 'うさみ', first: '啓一郎', firstKana: 'けいいちろう' },
  { family: '上野', familyKana: 'うえの', first: '一也', firstKana: 'かずや' },
  { family: '畠山', familyKana: 'はたけやま', first: '亘', firstKana: 'わたる' },
  { family: '渡邊', familyKana: 'わたなべ', first: '和星斗', firstKana: 'かずせいと' },
  { family: '王', familyKana: 'おう', first: '麗君', firstKana: 'れいくん' },
  { family: '船坂', familyKana: 'ふなさか', first: '渉', firstKana: 'わたる' },
  { family: '趙', familyKana: 'ちょう', first: '笑影', firstKana: 'しょうえい' },
  { family: '保原', familyKana: 'ほばら', first: '遊飛', firstKana: 'ゆうひ' },
  { family: '米岡', familyKana: 'よねおか', first: '裕矢', firstKana: 'ゆうや' },
  { family: '浅川', familyKana: 'あさかわ', first: '康則', firstKana: 'やすのり' },
  { family: '東谷', familyKana: 'ひがしたに', first: '裕介', firstKana: 'ゆうすけ' },
  { family: '原田', familyKana: 'はらだ', first: '安之', firstKana: 'やすゆき' },
  { family: '廣瀬', familyKana: 'ひろせ', first: '康夫', firstKana: 'やすお' },
  { family: '星', familyKana: 'ほし', first: '祐二', firstKana: 'ゆうじ' },
  { family: '加藤', familyKana: 'かとう', first: '陽介', firstKana: 'ようすけ' },
  { family: '河上', familyKana: 'かわかみ', first: '祐司', firstKana: 'ゆうじ' },
  { family: '川野邉', familyKana: 'かわのべ', first: '裕史', firstKana: 'ひろし' },
  { family: '廻', familyKana: 'めぐり', first: '裕一郎', firstKana: 'ゆういちろう' },
  { family: '桃沢', familyKana: 'ももざわ', first: '雄紀', firstKana: 'ゆうき' },
  { family: '中村', familyKana: 'なかむら', first: '陽一', firstKana: 'よういち' },
  { family: '折田', familyKana: 'おりた', first: '裕也', firstKana: 'ゆうや' },
  { family: '大滝', familyKana: 'おおたき', first: '勇輝', firstKana: 'ゆうき' },
  { family: '嶋', familyKana: 'しま', first: '康弘', firstKana: 'やすひろ' },
  { family: '祖川', familyKana: 'そがわ', first: '雄宇', firstKana: 'ゆう' },
  { family: '寺谷', familyKana: 'てらたに', first: '嘉孝', firstKana: 'よしたか' },
  { family: '臼井', familyKana: 'うすい', first: '義貴', firstKana: 'よしたか' },
  { family: '横山', familyKana: 'よこやま', first: '尉志', firstKana: 'ゆうじ' },
  { family: '八木橋', familyKana: 'やぎはし', first: '厚太', firstKana: 'こうた' },
  { family: '山川', familyKana: 'やまかわ', first: '遥暉', firstKana: 'はるき' },
  { family: '山崎', familyKana: 'やまざき', first: '勇児', firstKana: 'ゆうじ' },
  { family: '山﨑', familyKana: 'やまざき', first: '慎太郎', firstKana: 'しんたろう' },
  { family: '矢本', familyKana: 'やもと', first: '俊一', firstKana: 'しゅんいち' },
  { family: '周', familyKana: 'しゅう', first: '洋', firstKana: 'よう' },
  { family: '秋山', familyKana: 'あきやま', first: '康典', firstKana: 'やすのり' },
  { family: '都留', familyKana: 'つる', first: '靖士', firstKana: 'やすし' },
  { family: '安田', familyKana: 'やすだ', first: '肇', firstKana: 'はじめ' },
  { family: '宇野', familyKana: 'うの', first: '泰幸', firstKana: 'やすゆき' },
  { family: '遅', familyKana: 'おそ', first: '一嶺', firstKana: 'いちれい' },
  { family: '亀井', familyKana: 'かめい', first: '葉', firstKana: 'よう' },
  { family: '徳永', familyKana: 'とくなが', first: '佳也', firstKana: 'かや' },
  { family: '藤田', familyKana: 'ふじた', first: '吉史', firstKana: 'よしふみ' },
  { family: '鎌田', familyKana: 'かまだ', first: '義英', firstKana: 'よしひで' },
  { family: '吉田', familyKana: 'よしだ', first: '実穂', firstKana: 'みほ' },
  { family: '山本', familyKana: 'やまもと', first: '佳宏', firstKana: 'よしひろ' },
  { family: '村田', familyKana: 'むらた', first: '芳光', firstKana: 'よしみつ' },
  { family: '大塚', familyKana: 'おおつか', first: '好人', firstKana: 'よしひと' },
  { family: '平池', familyKana: 'ひらいけ', first: '佳之', firstKana: 'よしゆき' },
  { family: '林上', familyKana: 'はやしがみ', first: '芳行', firstKana: 'よしゆき' },
  { family: '松田', familyKana: 'まつだ', first: '洋輔', firstKana: 'ようすけ' },
  { family: '山本', familyKana: 'やまもと', first: '陽平', firstKana: 'ようへい' },
  { family: '秋元', familyKana: 'あきもと', first: '裕仁', firstKana: 'ひろひと' },
  { family: '福田', familyKana: 'ふくだ', first: '悠介', firstKana: 'ゆうすけ' },
  { family: '中島', familyKana: 'なかじま', first: '友樹', firstKana: 'ともき' },
  { family: '松澤', familyKana: 'まつざわ', first: '勇作', firstKana: 'ゆうさく' },
  { family: '宮野', familyKana: 'みやの', first: '勇治', firstKana: 'ゆうじ' },
  { family: '大嶋', familyKana: 'おおしま', first: '由真', firstKana: 'ゆま' },
  { family: '佐賀', familyKana: 'さが', first: '優子', firstKana: 'ゆうこ' },
  { family: '富田', familyKana: 'とみた', first: '夕子', firstKana: 'ゆうこ' },
  { family: '杉山', familyKana: 'すぎやま', first: '雄一', firstKana: 'ゆういち' },
  { family: '福田', familyKana: 'ふくだ', first: '優佳', firstKana: 'ゆうか' },
  { family: '山田', familyKana: 'やまだ', first: '有佳倫', firstKana: 'ありかりん' },
  { family: '監物', familyKana: 'けんもつ', first: '勇貴', firstKana: 'ゆうき' },
  { family: '桝田', familyKana: 'ますだ', first: '侑希', firstKana: 'ゆうき' },
  { family: '勝田', familyKana: 'かつた', first: '優輝', firstKana: 'ゆうき' },
  { family: '深山', familyKana: 'みやま', first: '優輝', firstKana: 'ゆうき' },
  { family: '大西', familyKana: 'おおにし', first: '佑樹', firstKana: 'ゆうき' },
  { family: '杉本', familyKana: 'すぎもと', first: '幸宏', firstKana: 'ゆきひろ' },
  { family: '斎藤', familyKana: 'さいとう', first: '幸生', firstKana: 'ゆきお' },
  { family: '佐藤', familyKana: 'さとう', first: '勇真', firstKana: 'ゆうま' },
  { family: '出澤', familyKana: 'でざわ', first: '佑磨', firstKana: 'ゆうま' },
  { family: '渡邉', familyKana: 'わたなべ', first: '美裕', firstKana: 'みひろ' },
  { family: '須山', familyKana: 'すやま', first: '雄介', firstKana: 'ゆうすけ' },
  { family: '齋藤', familyKana: 'さいとう', first: '佑育', firstKana: 'ゆういく' },
  { family: '関口', familyKana: 'せきぐち', first: '裕介', firstKana: 'ゆうすけ' },
  { family: '武本', familyKana: 'たけもと', first: '雄高', firstKana: 'ゆたか' },
  { family: '酒井', familyKana: 'さかい', first: '勇気', firstKana: 'ゆうき' },
  { family: '中島', familyKana: 'なかじま', first: '悠介', firstKana: 'ゆうすけ' },
  { family: '天野', familyKana: 'あまの', first: '勇太', firstKana: 'ゆうた' },
  { family: '渡邉', familyKana: 'わたなべ', first: '友哉', firstKana: 'ともや' },
  { family: '大西', familyKana: 'おおにし', first: '裕也', firstKana: 'ゆうや' },
  { family: '杉山', familyKana: 'すぎやま', first: '弓弦', firstKana: 'ゆづる' },
  { family: 'チャン', familyKana: 'ちゃん', first: 'ザウェイ', firstKana: 'ざうぇい' },
  // ===== 必要に応じてここに追記 =====
];


/**
 * フルネームを正規化する。
 * - 前後の空白をトリム
 * - 全角スペースを半角スペースに変換
 * - 連続する空白は 1 つの半角スペースにする
 */
export const normalizeNameKey = (raw: string): string => {
  if (!raw) return '';

  // 全角スペース → 半角スペース
  let s = raw.replace(/\u3000/g, ' ');

  // 連続する空白を 1 つに
  s = s.replace(/\s+/g, ' ');

  return s.trim();
};

/**
 * フルネーム（「姓 名」）→ ひらがな（読み） を持つ辞書。
 *
 * - キー: normalizeNameKey 済みのフルネーム（例: '山田 次郎'）
 * - 値  : 'やまだ じろう' のような「姓かな + 半角スペース + 名かな』
 *
 * ソート時はこの読みをそのまま使う想定。
 */
export const FULL_NAME_KANA_MAP: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const def of NAME_KANA_DEFINITIONS) {
    const key = normalizeNameKey(`${def.family} ${def.first}`);
    const value = `${def.familyKana} ${def.firstKana}`;
    map[key] = value;
  }
  return map;
})();

/**
 * 苗字 → ひらがな（読み） を持つベース辞書。
 *
 * - テストデータ・外字・ローマ字など、
 *   「名とペアではなく単独でも使いたいもの」をここに書く。
 */
const FAMILY_NAME_KANA_BASE: Record<string, string> = {
  // ===== テストデータ =====
  '加藤': 'かとう',
  '田中': 'たなか',
  '山田': 'やまだ',
  'でも': 'でも',
  'デモ': 'でも',
  'demo': 'でも',

  // --- ここからは外国風・ローマ字系など ---
  'suzuki': 'すずき',
  'キム': 'きむ',
  'グエン': 'ぐえん',
  '程': 'てい',
  '李': 'り',
  '王': 'おう',
  '趙': 'ちょう',
  '周': 'しゅう',
  '安': 'あん',
};

/**
 * 苗字 → ひらがな（読み） を持つ辞書。
 *
 * - キー: 苗字（姓）のみ
 * - 値  : かな（読み）
 *
 * NAME_KANA_DEFINITIONS から自動生成するので、
 * 姓の一覧はあちらを見れば「誰がいるか」が分かる。
 */
export const FAMILY_NAME_KANA_MAP: Record<string, string> = (() => {
  const map: Record<string, string> = { ...FAMILY_NAME_KANA_BASE };

  for (const def of NAME_KANA_DEFINITIONS) {
    if (!map[def.family]) {
      map[def.family] = def.familyKana;
    }
  }

  return map;
})();

/**
 * 名前 → ひらがな（読み） を持つベース辞書。
 *
 * - テスト用・汎用的に使いたい名だけをここに書く。
 *   社員の実データは NAME_KANA_DEFINITIONS から自動で入る。
 */
const FIRST_NAME_KANA_BASE: Record<string, string> = {
  // ===== テストデータ =====
  '太郎': 'たろう',
  '次郎': 'じろう',
  '翔太': 'しょうた',
  '大輔': 'だいすけ',
  '一郎': 'いちろう',
};

/**
 * 名前 → ひらがな（読み） を持つ辞書。
 *
 * - キー: 名（first_name）のみ（スペースを含むフルの「名前」部分）
 * - 値  : かな（読み）
 *
 * ソート時は「苗字の読み + 名前の読み」を使う。
 */
export const FIRST_NAME_KANA_MAP: Record<string, string> = (() => {
  const map: Record<string, string> = { ...FIRST_NAME_KANA_BASE };

  for (const def of NAME_KANA_DEFINITIONS) {
    if (!map[def.first]) {
      map[def.first] = def.firstKana;
    }
  }

  return map;
})();

/**
 * フルネームから「苗字（姓）」だけを取り出す。
 *
 * 想定フォーマット:
 *   - '山田 次郎'
 *   - '山田次郎'（スペースなしの場合は先頭の連続した漢字を姓とみなす、等の運用も可能）
 *
 * ここではシンプルに
 *   - normalizeNameKey で正規化
 *   - 半角スペースで split して先頭を姓とみなす
 */
export const extractFamilyName = (
  fullName: string | null | undefined,
): string | null => {
  if (!fullName) return null;

  const normalized = normalizeNameKey(String(fullName));
  if (!normalized) return null;

  // '山田 次郎' → ['山田', '次郎']
  const parts = normalized.split(' ');
  const family = parts[0] ?? '';

  return family || null;
};

/**
 * フルネームから「名前（名）」だけを取り出す。
 *
 * 想定フォーマットは extractFamilyName と同様。
 *   - '山田 次郎' → '次郎'
 *   - 'ベリエザ エイマヌエル ジョッシュ ゴンザレス'
 *       → 'エイマヌエル ジョッシュ ゴンザレス'
 */
export const extractFirstName = (
  fullName: string | null | undefined,
): string | null => {
  if (!fullName) return null;

  const normalized = normalizeNameKey(String(fullName));
  if (!normalized) return null;

  const parts = normalized.split(' ');
  if (parts.length <= 1) return null;

  const first = parts.slice(1).join(' ');

  return first || null;
};

/**
 * 苗字から かな（読み）を取得する。
 * 辞書に存在しない場合は null を返す。
 */
export const getKanaForFamilyName = (
  familyName: string | null | undefined,
): string | null => {
  if (!familyName) return null;

  const key = familyName.trim();
  if (!key) return null;

  const hit = FAMILY_NAME_KANA_MAP[key];
  return hit ?? null;
};

/**
 * 名前から かな（読み）を取得する。
 * 辞書に存在しない場合は null を返す。
 */
export const getKanaForFirstName = (
  firstName: string | null | undefined,
): string | null => {
  if (!firstName) return null;

  const key = firstName.trim();
  if (!key) return null;

  const hit = FIRST_NAME_KANA_MAP[key];
  return hit ?? null;
};

/**
 * フルネームから「苗字のかな（読み）」を取得するヘルパー。
 *
 * - フルネームから苗字を切り出す
 * - 苗字 → かなの辞書を引く
 * - 辞書にない場合は null
 */
export const getKanaForFullNameByFamily = (
  fullName: string | null | undefined,
): string | null => {
  const family = extractFamilyName(fullName);
  if (!family) return null;
  return getKanaForFamilyName(family);
};

/**
 * 「苗字 + 名前」でソートしたいので、
 * 実際には優先的に FULL_NAME_KANA_MAP を見に行き、
 * 見つからない場合は「苗字かな + 名前かな」を組み立てる。
 *
 * - FULL_NAME_KANA_MAP にある: それをそのまま返す（姓かな + 名かな）
 * - どちらかだけ辞書にある: わかる方だけかな、残りは元の文字列で補完
 * - どちらも辞書にない: 苗字だけのかなを返す（または null）
 */
export const getKanaForName = (
  fullName: string | null | undefined,
): string | null => {
  if (!fullName) return null;

  const normalized = normalizeNameKey(String(fullName));

  // 1. フルネーム単位での定義を優先
  const fullHit = FULL_NAME_KANA_MAP[normalized];
  if (fullHit) {
    return fullHit;
  }

  // 2. 姓・名を分けてそれぞれの辞書から組み立て
  const family = extractFamilyName(fullName);
  const first = extractFirstName(fullName);

  const familyKana = getKanaForFamilyName(family);
  const firstKana = getKanaForFirstName(first);

  // 苗字・名前ともに読みが取れた場合
  if (familyKana && firstKana) {
    return `${familyKana} ${firstKana}`;
  }

  // どちらか片方だけ辞書にある場合
  if (familyKana || firstKana) {
    const familyPart = familyKana ?? family ?? '';
    const firstPart = firstKana ?? first ?? '';
    const merged = `${familyPart}${firstPart ? ` ${firstPart}` : ''}`.trim();
    return merged || null;
  }

  // 3. どちらも辞書にない場合は、従来どおり「苗字だけのかな」を試す
  return getKanaForFullNameByFamily(fullName);
};
